import { get, reduce, upperFirst, findIndex } from 'lodash';
import { compose, withState, withProps, withHandlers } from 'recompose';

import Stripe from '../../utils/stripe';
import { generateShortId } from '../../utils/helpers';
import analytics from '../../applicationServices/analytics/BookingAnalytics';

const style = {
  base: {
    lineHeight: '40px',
    backgroundColor: '#fff',
    padding: '0 10px',
    marginLeft: '10px',
    fontSize: '14px',
    fontFamily: 'Open Sans, sans-serif',
    color: '#4F4442',

    '::placeholder': {
      color: 'rgba(0,0,0,0.5)',
    },
  },
};

const fonts = [
  {
    src: 'local("Open Sans"), url("https://fonts.gstatic.com/s/opensans/v17/mem8YaGs126MiZpBA-UFVZ0bf8pkAg.woff2")',
    family: 'Open Sans',
    style: 'normal',
  },
];

const focusOrder = ['cardNumber', 'cardExpiry', 'cardCvc', 'zipCode'];

export default ({
  cardNumberId = 'stripeCardNumberInput',
  cardCvcId = 'stripeCardCvcInput',
  cardExpiryId = 'stripeCardExpiryInput',
  zipCodeId = 'stripeZipCodeInput',
}) => {
  const stripe = new Stripe();
  return compose(
    withState('generatedInputId', '_', () => generateShortId()),
    withState('stripeInputErrors', 'setStripeInputErrors', {}),
    withState('hasErrors', 'setHasErrors', false),
    withState('cardBrand', 'setCardBrand', 'unknown'),
    withState('cardNumber', 'setCardNumber', null),
    withState('cardExpiry', 'setCardExpiry', null),
    withState('cardCvc', 'setCardCvc', null),
    withState('zipCode', 'setZipCode', null),
    withProps(props => ({
      cardNumberId: `${cardNumberId}_${props.generatedInputId}`,
      cardCvcId: `${cardCvcId}_${props.generatedInputId}`,
      cardExpiryId: `${cardExpiryId}_${props.generatedInputId}`,
      zipCodeId: `${zipCodeId}_${props.generatedInputId}`,
    })),
    withHandlers({
      validateZipCode: props => () => {
        if (!props.zipCode) {
          return true;
        }

        const zipCode = props.zipCode.value;
        const errors = {
          ...props.stripeInputErrors,
          zipCode: zipCode ? null : 'Zip/postal code is required',
        };
        const hasErrors = reduce(errors, (memo, err) => (memo || err), false);

        props.setStripeInputErrors(errors);
        props.setHasErrors(hasErrors);
        return !hasErrors;
      },
      onChange: props => (event) => {
        const error = get(event, 'error.message');
        const cardBrand = event.elementType === 'cardNumber' ? event.brand : props.cardBrand;
        const errors = {
          ...props.stripeInputErrors,
          [event.elementType]: error,
        };

        props.setStripeInputErrors(errors);
        props.setHasErrors(reduce(errors, (memo, err) => (memo || err), false));
        props.setCardBrand(cardBrand);

        if (event.complete) {
          // We have a well-formed and potentially completed input value. We can fire event for analytics
          analytics.fillCardInfoField(event.elementType);

          const activeFocusIdx = findIndex(focusOrder, item => item === event.elementType);
          const nextFocusName = focusOrder[activeFocusIdx + 1];
          const el = props[nextFocusName];

          // amex allows both 3 and 4 digit CVCs but event.complete is true even with 3 digits,
          // so we can't rely on that field and we can't autofocus to the next element
          // once amex cvc is marked as complete
          const isAmexCvcChange = cardBrand === 'amex' && event.elementType === 'cardCvc';

          if (el && !isAmexCvcChange) {
            el.focus();
          }
        }
      },
    }),
    withHandlers({
      setUpElement: props => (elements, type, config) => {
        const element = elements.create(type, config);
        element.mount(`#${props[`${type}Id`]}`);
        element.addListener('change', props.onChange);
        props[`set${upperFirst(type)}`](element);
      },
      disposeElement: props => (type) => {
        props[type].removeListener('change', props.onChange);
        props[type].focus();
        props[type].blur();
        props[type].destroy();
      },
      clearElement: props => (type) => {
        if (props[type]) {
          props[type].clear();
        }
      },
    }),
    withHandlers({
      mountStripeInputs: props => () => {
        const elements = stripe.elements({ fonts });
        props.setUpElement(elements, 'cardNumber', {
          style: { base: { ...style.base, paddingLeft: '40px' } },
          placeholder: 'Card Number',
        });
        props.setUpElement(elements, 'cardExpiry', { style });
        props.setUpElement(elements, 'cardCvc', { style });

        const zipCode = document.getElementById(props.zipCodeId);
        props.setZipCode(zipCode);
      },
      unmountStripeInputs: props => () => {
        props.disposeElement('cardNumber');
        props.disposeElement('cardExpiry');
        props.disposeElement('cardCvc');
      },
      clearStripeInputs: props => () => {
        props.clearElement('cardNumber');
        props.clearElement('cardExpiry');
        props.clearElement('cardCvc');
        props.setStripeInputErrors({});
        props.setHasErrors(false);
      },
      setStripeInputsDisabled: props => (state) => {
        props.cardNumber.update({ disabled: state });
        props.cardExpiry.update({ disabled: state });
        props.cardCvc.update({ disabled: state });

        if (state) {
          props.zipCode.setAttribute('disabled', state);
        } else {
          props.zipCode.removeAttribute('disabled');
        }
      },
      focusInput: props => (name) => {
        props[name].focus();
      },
    }),
  );
};



// WEBPACK FOOTER //
// ./src/app/reservation/containers/withStripeInput.js